import { useEffect, useState } from 'react';
import { Button, Input, Select, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, EmptyState } from '../../../../shared/components';
import { PlusIcon, EditIcon, TrashIcon } from '../../../../shared/icons';
import { getResultsRepository } from '../../../../shared/services/results/repositoryFactory';
import { ClientMapping, CreateClientMappingInput, ServiceType } from '../../../../shared/services/results/types';

const repo = getResultsRepository();
const emptyForm: CreateClientMappingInput = { clienteDatosTabla: '', clienteResultadosMes: '', nombreCorto: '', tipoServicio: null, isActive: true };

/**
 * Parámetros > Mapeo de Clientes: resuelve el riesgo real detectado en el
 * análisis del Excel - el nombre de cliente en "datos tabla" (corto, ej.
 * "COL ARMENIA") no coincide textualmente con el de la columna en
 * "resultados mes" (largo, ej. "COLTABACO ARMENIA"). Antes se resolvía con
 * un VLOOKUP manual que fallaba en silencio; aquí queda explícito.
 *
 * También vive aquí el Tipo de Servicio de cada cliente - verificado con
 * datos reales (Junio, 3.038 combinaciones cliente-cuenta): depende
 * ÚNICAMENTE del cliente, nunca de la cuenta, así que no hace falta una
 * tabla maestra aparte. El import lo usa para generar "Datos Tabla"
 * automáticamente a partir de la única hoja que ahora trae el archivo.
 */
export function ClientMappingTab() {
  const toast = useToast();
  const [rows, setRows] = useState<ClientMapping[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ClientMapping | null>(null);
  const [form, setForm] = useState<CreateClientMappingInput>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<ClientMapping | null>(null);

  const load = () => {
    setIsLoading(true);
    Promise.all([repo.listClientMappings(), repo.listServiceTypes()]).then(([r, st]) => {
      setRows(r);
      setServiceTypes(st);
      setIsLoading(false);
    });
  };
  useEffect(load, []);

  const openCreate = () => {
    setForm(emptyForm);
    setCreating(true);
  };
  const openEdit = (r: ClientMapping) => {
    setForm({ clienteDatosTabla: r.clienteDatosTabla, clienteResultadosMes: r.clienteResultadosMes, nombreCorto: r.nombreCorto, tipoServicio: r.tipoServicio, isActive: r.isActive });
    setEditing(r);
  };
  const close = () => {
    setCreating(false);
    setEditing(null);
  };

  const save = async () => {
    if (!form.clienteDatosTabla || !form.clienteResultadosMes) {
      toast.show('Los dos nombres de cliente son obligatorios.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      if (editing) {
        await repo.updateClientMapping(editing.id, form);
        toast.show('Mapeo actualizado.');
      } else {
        await repo.createClientMapping(form);
        toast.show('Mapeo agregado.');
      }
      close();
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const serviceTypeOptions = [{ value: '', label: 'Sin definir' }, ...serviceTypes.map((s) => ({ value: s.nombre, label: s.nombre }))];

  const columns: Column<ClientMapping>[] = [
    { key: 'datosTabla', header: 'Cliente en "Datos Tabla"', render: (r) => r.clienteDatosTabla, sortable: true, sortValue: (r) => r.clienteDatosTabla },
    { key: 'resultadosMes', header: 'Columna en "Resultados Mes"', render: (r) => r.clienteResultadosMes },
    { key: 'corto', header: 'Nombre corto', render: (r) => r.nombreCorto },
    { key: 'tipoServicio', header: 'Tipo de Servicio', render: (r) => (r.tipoServicio ? <Badge tone="info">{r.tipoServicio}</Badge> : <Badge tone="warning">Sin definir</Badge>) },
    { key: 'activo', header: 'Estado', render: (r) => <Badge tone={r.isActive ? 'success' : 'neutral'}>{r.isActive ? 'Activo' : 'Inactivo'}</Badge> },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(r)} className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(r)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Button leftIcon={<PlusIcon width={18} height={18} />} onClick={openCreate}>
        Agregar mapeo de cliente
      </Button>

      {rows.length === 0 && !isLoading ? (
        <EmptyState icon="🔗" title="Sin mapeos" description="Agrega el primer mapeo para que el import reconozca correctamente los clientes y su Tipo de Servicio." />
      ) : (
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} isLoading={isLoading} pageSize={10} pageSizeOptions={[10, 25, 50]} emptyTitle="Sin mapeos" />
      )}

      <Modal open={creating || !!editing} onClose={close} title={editing ? 'Editar mapeo' : 'Agregar mapeo'}>
        <div className="space-y-3">
          <Input label='Cliente en "Datos Tabla"' hint="Nombre corto, tal cual aparece en el histórico." value={form.clienteDatosTabla} onChange={(e) => setForm((f) => ({ ...f, clienteDatosTabla: e.target.value }))} />
          <Input label='Columna en "Resultados Mes"' hint="Nombre largo, tal cual lo nombra Contabilidad en el Excel." value={form.clienteResultadosMes} onChange={(e) => setForm((f) => ({ ...f, clienteResultadosMes: e.target.value }))} />
          <Input label="Nombre corto (para mostrar)" value={form.nombreCorto} onChange={(e) => setForm((f) => ({ ...f, nombreCorto: e.target.value }))} />
          <Select
            label="Tipo de Servicio"
            options={serviceTypeOptions}
            value={form.tipoServicio ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, tipoServicio: e.target.value || null }))}
          />
          <p className="text-xs text-ink-400">Se usa para generar "Datos Tabla" automáticamente al importar - un cliente siempre tiene un único Tipo de Servicio.</p>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={close}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Eliminar mapeo"
        description={`¿Eliminar el mapeo de "${deleting?.clienteDatosTabla}"? Los próximos imports no podrán reconocer automáticamente ese cliente hasta que se vuelva a mapear.`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await repo.deleteClientMapping(deleting.id);
          setDeleting(null);
          load();
        }}
      />
    </div>
  );
}
